export const CURATED_ETP_GROUPS = ['Amundi ETPs', 'WisdomTree ETPs', 'Basket ETPs'];

export const COMMODITY_ETC_BUCKETS = new Set([
  'commodity_broad',
  'energy',
  'precious_metals',
  'industrial_metals',
  'agriculture',
  'livestock',
  'gold',
]);

export const BUCKET_LABELS = {
  equity_world_incl_em: 'World (incl. EM)',
  equity_europe: 'Europe',
  equity_em: 'Emerging markets',
  equity_japan: 'Japan',
  equity_pacific: 'Pacific ex-Japan',
  equity_india: 'India',
  equity_us: 'United States',
  equity_small_cap: 'World small cap',
  factor_value: 'Factor: value',
  factor_momentum: 'Factor: momentum',
  factor_quality: 'Factor: quality',
  factor_min_vol: 'Factor: min vol',
  equity_world: 'World (EUR hedged)',
  cash_eur: 'EUR cash',
  govt_eur: 'Eurozone govt',
  inflation_linked_eur: '€ linkers',
  corp_ig_eur: '€ IG credit',
  corp_hy_eur: '€ high yield',
  em_debt_usd: 'EM debt (USD)',
  govt_us_long: 'US Treasuries 20+',
  real_estate: 'Real estate',
  commodity_broad: 'Commodities (broad)',
  energy: 'Energy',
  precious_metals: 'Precious metals',
  industrial_metals: 'Industrial metals',
  agriculture: 'Agriculture',
  livestock: 'Livestock',
  gold: 'Gold',
  crypto_bitcoin: 'Bitcoin (physical)',
  us_volatility: 'US volatility (VIX futures)',
  us_commodity_broad: 'US commodities (index)',
  us_carbon: 'US carbon (index)',
  us_energy_crude: 'US crude oil (structured)',
  us_mlp: 'US MLPs (index)',
  us_bdc: 'US BDCs (index)',
};

export const ZOOM_RANGES = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '2Y', days: 730 },
  { label: '5Y', days: 1825 },
  { label: 'All', days: null },
];

export const DEFAULT_ZOOM = '1Y';

export const SMA_DEFS = [
  { key: 'sma10', period: 10, label: '10', color: '#A15217', lineWidth: 1 },
  { key: 'sma21', period: 21, label: '21', color: '#173CA1', lineWidth: 1 },
  { key: 'sma50', period: 50, label: '50', color: '#8D17A1', lineWidth: 1 },
  { key: 'sma200', period: 200, label: '200', color: '#A12017', lineWidth: 2 },
];

export const INITIAL_SMA_ACTIVE = Object.fromEntries(SMA_DEFS.map((d) => [d.key, false]));

export const REPLICATION_LABELS = {
  physical: 'Physical',
  futures: 'Futures',
  synthetic: 'Synthetic',
};

export const REPLICATION_TITLES = {
  physical: 'Physical backing (metal bars or index replication)',
  futures: 'Futures roll strategy — curve shape affects performance',
  synthetic: 'Synthetic / index-linked note (not exchange futures roll)',
};

export function fmtPrice(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtSpreadPct(spreadRelative) {
  if (spreadRelative == null || Number.isNaN(spreadRelative)) return '—';
  return `${(Number(spreadRelative) * 100).toFixed(2)}%`;
}

export function fmtCostPct(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(2)}%`;
}

/** Round-trip cost (RTC) = TER + 2 × spread; TER and result in percent points. */
export function syntheticRtc(ter, spreadRelative) {
  if (ter == null || spreadRelative == null || Number.isNaN(ter) || Number.isNaN(spreadRelative)) {
    return null;
  }
  return Number(ter) + 2 * Number(spreadRelative) * 100;
}

export function fmtReplication(value) {
  if (!value) return '—';
  return REPLICATION_LABELS[value] || value;
}

export function fmtBucket(value) {
  if (!value) return '—';
  return BUCKET_LABELS[value] || value.replace(/_/g, ' ');
}

export function flattenCuratedEtps(groups) {
  if (!Array.isArray(groups)) return [];
  const allowed = new Set(CURATED_ETP_GROUPS);
  return groups
    .filter((group) => allowed.has(group.sector_group))
    .flatMap((group) =>
      (group.symbols || []).map((symbol) => ({
        ...symbol,
        sector_group: symbol.sector_group ?? group.sector_group,
        category: symbol.category ?? group.category,
        yahoo_key: symbol.yahoo_key ?? group.yahoo_key,
      })),
    );
}

/** Curated listings with category `etf` only (excludes ETCs and ETNs). */
export function flattenCuratedEtfs(groups) {
  return flattenCuratedEtps(groups).filter((row) => row.category === 'etf');
}

/** Curated ETN listings (crypto, US structured notes, etc.). */
export function flattenCuratedEtns(groups) {
  return flattenCuratedEtps(groups).filter((row) => row.category === 'etn');
}

export function flattenCommodityEtcs(groups) {
  return flattenCuratedEtps(groups).filter(
    (row) => row.category === 'etc' && row.bucket && COMMODITY_ETC_BUCKETS.has(row.bucket),
  );
}

export function rowKey(row) {
  return `${row.sector_group}-${row.symbol}`;
}

export function applyVisibleRange(chart, barData, rangeLabel) {
  if (!chart || !barData?.length) return;

  const spec = ZOOM_RANGES.find((r) => r.label === rangeLabel);
  if (!spec || spec.days == null) {
    chart.timeScale().fitContent();
    return;
  }

  const to = barData[barData.length - 1].time;
  const end = new Date(`${to}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - spec.days);
  const from = start.toISOString().slice(0, 10);

  try {
    chart.timeScale().setVisibleRange({ from, to });
  } catch {
    chart.timeScale().fitContent();
  }
}
